/**
 * События на весь день из календаря устройства: дни рождения, праздники,
 * отпуск.
 *
 * Сверка встреч берёт только события со временем — им есть место в сетке дня.
 * События на весь день времени не занимают и в бюджет дня не входят, но знать
 * о них нужно: день рождения друга — повод позвонить, а не слот в расписании.
 * Поэтому они не становятся встречами, а показываются строкой над днём —
 * только для чтения.
 *
 * Хранятся в настройках устройства, рядом с состоянием сверки: это зеркало
 * календаря телефона, а не данные человека. Наружу — ни в копию, ни на сервер —
 * оно не уезжает и пересобирается при каждой сверке.
 */

import { useLiveQuery } from 'dexie-react-hooks';

import { db } from '@/shared/db/database';
import { getCurrentUserId } from '@/shared/db/session-context';
import { addDays, type IsoDate, toIsoDate } from '@/shared/lib/datetime';

import { birthdayAge, type ContactBirthday } from './contacts';

/** Событие на весь день так, как его отдаёт календарь устройства. */
export interface SystemAllDayEvent {
  calendarId: string;
  title: string;
  /** Первый день, включительно. */
  from: IsoDate;
  /** Последний день, включительно. */
  to: IsoDate;
}

/** Что показывается над днём. */
export interface AllDayItem {
  date: IsoDate;
  title: string;
  /** День рождения — показывается тортом. */
  birthday: boolean;
  /** Сколько исполняется — если год рождения есть в контакте телефона. */
  age?: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Многодневное дальше этого не раскладывается: это уже не событие, а сезон. */
const MAX_SPAN_DAYS = 62;

/**
 * Дата события на весь день.
 *
 * Android хранит такие события от полуночи по UTC: в Москве «5 октября»
 * начинается в 03:00, а к западу от Гринвича — накануне вечером. Поэтому
 * ровная полночь по UTC читается как дата календаря, а не как местное время.
 */
function allDayDate(ms: number): IsoDate {
  return ms % DAY_MS === 0 ? new Date(ms).toISOString().slice(0, 10) : toIsoDate(new Date(ms));
}

/** Событие устройства на весь день. `null` — событие со временем или без календаря. */
export function toAllDayEvent(raw: {
  calendarId: string | null;
  title: string | null;
  startDate: number;
  endDate: number;
  isAllDay: boolean;
}): SystemAllDayEvent | null {
  if (!raw.isAllDay || raw.calendarId === null) return null;

  const from = allDayDate(raw.startDate);
  // Конец — следующая полночь, не входящая в событие.
  const last = addDays(allDayDate(raw.endDate), -1);

  return {
    calendarId: raw.calendarId,
    title: raw.title?.trim() || 'Без названия',
    from,
    to: last < from ? from : last,
  };
}

const BIRTHDAY = /рожд|birthday/i;

/**
 * Раскладывает события по дням окна сверки.
 *
 * День рождения узнаётся и по названию, и по календарю: у календаря дней
 * рождения контактов в названии события часто стоит одно имя.
 */
export function expandAllDay(
  events: readonly SystemAllDayEvent[],
  calendars: readonly { id: string; title: string }[],
  from: IsoDate,
  to: IsoDate,
  contacts: readonly ContactBirthday[] = [],
): AllDayItem[] {
  const calendarTitle = new Map(calendars.map((calendar) => [calendar.id, calendar.title]));
  const items: AllDayItem[] = [];

  for (const event of events) {
    const birthday =
      BIRTHDAY.test(event.title) || BIRTHDAY.test(calendarTitle.get(event.calendarId) ?? '');

    let date = event.from < from ? from : event.from;
    const last = event.to > to ? to : event.to;
    for (let day = 0; date <= last && day < MAX_SPAN_DAYS; day += 1) {
      const age = birthday ? birthdayAge({ date, title: event.title }, contacts) : null;
      items.push({ date, title: event.title, birthday, ...(age === null ? {} : { age }) });
      date = addDays(date, 1);
    }
  }

  return items.sort((a, b) => a.date.localeCompare(b.date));
}

const storageKey = (userId: string) => `calendar.allday:${userId}`;

export async function readAllDayItems(userId: string): Promise<AllDayItem[]> {
  const row = await db.settings.get(storageKey(userId));
  return Array.isArray(row?.value) ? (row.value as AllDayItem[]) : [];
}

export async function writeAllDayItems(userId: string, items: AllDayItem[]): Promise<void> {
  await db.settings.put({ key: storageKey(userId), value: items, updatedAt: Date.now() });
}

export async function clearAllDayItems(userId: string): Promise<void> {
  await db.settings.delete(storageKey(userId));
}

/** События на весь день одного дня — для показа над ним. */
export function useAllDayItems(date: IsoDate): AllDayItem[] {
  return (
    useLiveQuery(async () => {
      const userId = getCurrentUserId();
      if (!userId) return [];
      return (await readAllDayItems(userId)).filter((item) => item.date === date);
    }, [date]) ?? []
  );
}
